import { useEffect, useState } from 'react'
import { SQUARE_FEET, unitChoices, type UnitChoice } from './area'
import api from '@/lib/axiosInstance'

/**
 * The form's own dropdown lists, for the review to offer the same choices.
 *
 * `/listings/criterias` is one call that returns every list; it is fetched
 * once per page and shared. Property sub-types depend on the type chosen, so
 * they are fetched per type and cached the same way.
 */
export interface Choice {
    value: number
    label: string
    /** The district a subdistrict belongs to, or the state a district is in. */
    parentId?: number
}

interface Criterias {
    listingTypes: Choice[]
    projectTypes: Choice[]
    forSaleOrRentOptions: Choice[]
    rentalTypes: Choice[]
    propertyTypes: Choice[]
    propertyUnitTypes: Choice[]
    tenures: Choice[]
    titleTypes: Choice[]
    furnishingConditions: Choice[]
    zoning: Choice[]
    districts: Choice[]
    subdistricts: Choice[]
    /** Rows of their own, kept per state. */
    landTitleTypes: Choice[]
    /** The units the form offers for each area, as it sends them. */
    landAreaUnits: UnitChoice[]
    builtUpUnits: UnitChoice[]
}

const EMPTY: Criterias = {
    listingTypes: [],
    projectTypes: [],
    forSaleOrRentOptions: [],
    rentalTypes: [],
    propertyTypes: [],
    propertyUnitTypes: [],
    tenures: [],
    titleTypes: [],
    furnishingConditions: [],
    zoning: [],
    districts: [],
    subdistricts: [],
    landTitleTypes: [],
    landAreaUnits: [SQUARE_FEET],
    builtUpUnits: [SQUARE_FEET],
}

/** The API returns `{ id, name }` for some lists and `{ value, label }` for others. */
const toChoices = (rows: unknown): Choice[] =>
    Array.isArray(rows)
        ? rows
              .map((r) => {
                  const row = r as { id?: number; name?: string; value?: number; label?: string }
                  const value = row.value ?? row.id
                  const label = row.label ?? row.name
                  return value !== undefined && label ? { value: Number(value), label: String(label) } : null
              })
              .filter((x): x is Choice => x !== null)
        : []

/** Districts carry their state, subdistricts their district, for the dependent lists. */
const toParented = (rows: unknown, parentKey: string): Choice[] =>
    Array.isArray(rows)
        ? rows
              .map((r): Choice | null => {
                  const row = r as Record<string, unknown>
                  if (typeof row.id !== 'number' || typeof row.name !== 'string') return null
                  return { value: row.id, label: row.name, parentId: typeof row[parentKey] === 'number' ? (row[parentKey] as number) : undefined }
              })
              .filter((x): x is Choice => x !== null)
        : []

let load: Promise<Criterias> | null = null
const loadCriterias = () =>
    (load ??= api
        .get('/listings/criterias')
        .then(({ data }) => ({
            listingTypes: toChoices(data?.listingTypes),
            projectTypes: toChoices(data?.projectTypes),
            forSaleOrRentOptions: toChoices(data?.forSaleOrRentOptions),
            rentalTypes: toChoices(data?.rentalTypes),
            propertyTypes: toChoices(data?.propertyTypes),
            propertyUnitTypes: toChoices(data?.propertyUnitTypes),
            tenures: toChoices(data?.tenures),
            titleTypes: toChoices(data?.titleTypes),
            furnishingConditions: toChoices(data?.furnishingConditions),
            zoning: toChoices(data?.zoning),
            districts: toParented(data?.districts, 'stateId'),
            subdistricts: toParented(data?.subdistricts, 'districtId'),
            landTitleTypes: toParented(data?.landTitleTypes, 'stateId'),
            landAreaUnits: unitChoices(data?.landAreaUnits),
            builtUpUnits: unitChoices(data?.builtUpUnits),
        }))
        .catch(() => {
            load = null
            return EMPTY
        }))

export function useCriterias() {
    const [lists, setLists] = useState<Criterias>(EMPTY)
    useEffect(() => {
        loadCriterias().then(setLists)
    }, [])
    return lists
}

const subTypeLoads = new Map<number, Promise<Choice[]>>()
const loadSubTypes = (propertyTypeId: number) => {
    if (!subTypeLoads.has(propertyTypeId)) {
        subTypeLoads.set(
            propertyTypeId,
            api
                .get(`/listings/properties/types/${propertyTypeId}`)
                .then(({ data }) => toChoices(data?.propertySubTypes ?? data?.subTypes))
                .catch(() => {
                    subTypeLoads.delete(propertyTypeId)
                    return []
                }),
        )
    }
    return subTypeLoads.get(propertyTypeId)!
}

/** The sub-types of the chosen property type, empty until one is chosen. */
export function usePropertySubTypes(propertyTypeId: number | null) {
    const [subTypes, setSubTypes] = useState<Choice[]>([])
    useEffect(() => {
        if (!propertyTypeId) return setSubTypes([])
        let cancelled = false
        loadSubTypes(propertyTypeId).then((rows) => !cancelled && setSubTypes(rows))
        return () => {
            cancelled = true
        }
    }, [propertyTypeId])
    return subTypes
}
